import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Table, Button, Modal, Form, Input, Upload,
  message, Space, Tag, Popconfirm, Alert, Spin, Row, Col, Empty, Select
} from 'antd';
import { UploadOutlined, PlusOutlined, SearchOutlined, LoadingOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import QuillEditor from '../../quill/QuillEditor';
import { S3_CONFIG } from '../../config/s3';
import {
  createBlog,
  deleteBlog,
  updateBlog,
  fetchBlogs,
  setHomepageOrder
} from '../../store/features/dashboard/blogSlice';
import { debounce, beforeUploadValidation } from '../../lib/utils';

const { Option } = Select;

const Blogs = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [existingImages, setExistingImages] = useState([]);
  const [content, setContent] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentBlogId, setCurrentBlogId] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [deletedFilter, setDeletedFilter] = useState(''); 
  const [homepageFilter, setHomepageFilter] = useState(''); 
  const searchInputRef = useRef(null);
  const dispatch = useDispatch();
  const [orderModalVisible, setOrderModalVisible] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);

  const {
    blogs,
    loading,
    error,
    total = 0,
    currentPage = 1,
    pageSize = 10,
  } = useSelector((state) => state.blogs);

  const handleOrderClick = (blog) => {
    setSelectedBlog({
      ...blog,
      homepageOrder: blog.homepage_order || 0
    });
    setOrderModalVisible(true);
  };

  const handleOrderUpdate = async () => {
    try {
      const existingBlog = blogs.find(s =>
        s.homepage_order === selectedBlog.homepageOrder &&
        s.id !== selectedBlog.id
      );

      if (existingBlog && selectedBlog.homepageOrder > 0) {
        Modal.confirm({
          title: 'Position Already Taken',
          content: `Position ${selectedBlog.homepageOrder} is currently held by "${existingBlog.title}". Do you want to replace it?`,
          onOk: async () => {
            try {
              await dispatch(setHomepageOrder({
                blogId: selectedBlog.id,
                order: selectedBlog.homepageOrder
              })).unwrap();

              message.success('Homepage positions updated!');
              fetchData(currentPage);
              setOrderModalVisible(false);
            } catch (err) {
              message.error(err.message || 'Failed to update positions');
            }
          }
        });
      } else {
        await dispatch(setHomepageOrder({
          blogId: selectedBlog.id,
          order: selectedBlog.homepageOrder
        })).unwrap();

        message.success(
          selectedBlog.homepageOrder > 0
            ? `Blog set to position ${selectedBlog.homepageOrder}`
            : 'Blog removed from homepage'
        );
        fetchData(currentPage);
        setOrderModalVisible(false);
      }
    } catch (err) {
      message.error(err.message || 'Failed to update position');
    }
  };

  const normFile = (e) => {
    return Array.isArray(e) ? e : e?.fileList;
  };

  const fetchData = useCallback((page, size, search = '', deleted = deletedFilter, homepage = homepageFilter) => {
    setIsSearching(true);
    dispatch(fetchBlogs({
      page,
      limit: size || pageSize,
      search,
      deleted: deleted === 'true' ? 'true' : '',
      homepage: homepage === 'true' ? 'true' : ''
    })).finally(() => {
      setIsSearching(false);
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    });
  }, [dispatch, pageSize, deletedFilter, homepageFilter]);

  const debouncedSearch = useCallback(
    debounce((searchValue) => {
      if (searchValue.length >= 2 || searchValue.length === 0) {
        fetchData(1, pageSize, searchValue);
      }
    }, 500),
    [fetchData, pageSize]
  );

  const handleSearch = (value, event) => {
    setSearchText(value);
    if (event && event.type === 'change') {
      debouncedSearch(value);
    } else {
      fetchData(1, pageSize, value);
    }
  };

  const handleClearSearch = () => {
    setSearchText('');
    fetchData(1, pageSize, '');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  useEffect(() => {
    fetchData(currentPage, pageSize, searchText);
  }, [currentPage, pageSize, deletedFilter, homepageFilter]);

  const handlePageChange = (page, size) => {
    fetchData(page, size);
  };

  const resetModal = () => {
    setModalVisible(false);
    setContent('');
    setIsEditMode(false);
    setCurrentBlogId(null);
    setExistingImages([]);
    form.resetFields();
    setSubmitLoading(false);
  };

  const handleDelete = async (blogId) => {
    try {
      await dispatch(deleteBlog(blogId)).unwrap();
      message.success('Blog soft deleted successfully!');
      if (blogs.length === 1 && currentPage > 1) {
        fetchData(currentPage - 1);
      } else {
        fetchData(currentPage);
      }
    } catch (error) {
      message.error(error.message || 'Failed to soft delete blog');
    }
  };

  const handleEdit = (blog) => {
    setIsEditMode(true);
    setCurrentBlogId(blog.id);
    setContent(blog.content || '');
    setModalVisible(true);

    const imageFields = {};
    if (Array.isArray(blog.images)) {
      blog.images.forEach((image, index) => {
        if (image && typeof image === 'object' && image.url && Number.isInteger(image.order)) {
          const imageKey = `image${image.order + 1}`;
          imageFields[imageKey] = [{
            uid: `-${index}`,
            name: image.url.split('/').pop(),
            status: 'done',
            url: `${S3_CONFIG.publicUrl}/${image.url}`,
          }];
        }
      });
    }

    for (let i = 1; i <= 3; i++) {
      if (!imageFields[`image${i}`]) {
        imageFields[`image${i}`] = [];
      }
    }

    form.setFieldsValue({
      title: blog.title || '',
      author_name: blog.author_name || '',
      category: blog.category || '',
      tags: blog.popular_tags?.join(', ') || '',
      ...imageFields,
    });
  };

  const handleBlogSubmit = async (values, isEdit = false) => {
    try {
      setSubmitLoading(true);

      const requiredFields = [
        { field: 'title', message: 'Please add title to the blog' },
        { field: 'author_name', message: 'Please add author name to the blog' },
        { field: 'category', message: 'Please add category to the blog' },
      ];

      for (const item of requiredFields) {
        if (!values[item.field]?.trim()) {
          message.error(item.message);
          setSubmitLoading(false);
          return;
        }
      }

      const imageFields = ['image1', 'image2', 'image3'];
      const missingImages = imageFields.filter(field => !values[field] || values[field].length === 0);
      if (missingImages.length > 0) {
        message.error(`Please upload all 3 images (missing: ${missingImages.map(f => f.replace('image', 'Image ')).join(', ')})`);
        setSubmitLoading(false);
        return;
      }

      if (!content?.trim()) {
        message.error('Please add content to the blog');
        setSubmitLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append('title', values.title);
      formData.append('author_name', values.author_name);
      formData.append('category', values.category);
      formData.append('content', content);
      if (values.tags) {
        const tagsArray = values.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
        formData.append('popular_tags', JSON.stringify(tagsArray));
      }

      const newFiles = [];
      const fileOrders = [];
      const existingImages = [];

      imageFields.forEach((fieldName, index) => {
        const fileList = values[fieldName];
        if (fileList && fileList[0]) {
          const fileInfo = fileList[0];
          const order = index;

          if (fileInfo.originFileObj) {
            newFiles.push(fileInfo.originFileObj);
            fileOrders.push(order);
          } else if (fileInfo.url) {
            const cleanUrl = fileInfo.url.replace(`${S3_CONFIG.publicUrl}/`, '');
            existingImages.push({ url: cleanUrl, order });
          }
        }
      });

      if (newFiles.length + existingImages.length !== 3) {
        message.error('Exactly 3 images are required (new or existing)');
        setSubmitLoading(false);
        return;
      }
      if (fileOrders.length > 0) {
        const uniqueOrders = new Set(fileOrders);
        if (uniqueOrders.size !== fileOrders.length || fileOrders.some(order => order < 0 || order > 2)) {
          message.error('Invalid file orders: must be unique integers between 0 and 2');
          setSubmitLoading(false);
          return;
        }
      }
      if (existingImages.length > 0) {
        const uniqueOrders = new Set(existingImages.map(img => img.order));
        if (uniqueOrders.size !== existingImages.length || existingImages.some(img => img.order < 0 || img.order > 2)) {
          message.error('Invalid existing image positions: must be unique integers between 0 and 2');
          setSubmitLoading(false);
          return;
        }
      }

      newFiles.forEach(file => {
        formData.append('files', file);
      });
      if (fileOrders.length > 0) {
        formData.append('fileOrders', JSON.stringify(fileOrders));
      }
      if (existingImages.length > 0) {
        formData.append('existingImagePositions', JSON.stringify(existingImages));
      }

      let response;
      if (isEdit) {
        response = await dispatch(updateBlog({ blogId: currentBlogId, formData })).unwrap();
      } else {
        response = await dispatch(createBlog(formData)).unwrap();
      }

      if (response && response.error) {
        throw new Error(response.message || 'An error occurred');
      }

      message.success(`Blog ${isEdit ? 'updated' : 'created'} successfully!`);
      resetModal();
      fetchData(currentPage);
    } catch (error) {
      console.error('Submission error:', error);
      const errorMessage = error.message || `Failed to ${isEdit ? 'update' : 'create'} blog`;
      if (typeof errorMessage === 'string') {
        message.error(errorMessage);
      } else {
        message.error('An unexpected error occurred');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSubmit = () => {
    form.validateFields()
      .then(values => {
        handleBlogSubmit(values, isEditMode);
      })
      .catch(error => {
        console.error('Validation error:', error);
        message.error('Please fill all required fields');
        setSubmitLoading(false);
      });
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 200,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: category => <Tag color="blue">{category || 'N/A'}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: status => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Tag>
      ),
    },
    {
      title: 'Homepage Position',
      dataIndex: 'homepage_order',
      key: 'homepage_order',
      width: 180,
      render: (order, record) => {
        const isConflict = blogs.filter(s =>
          s.homepage_order === order && s.id !== record.id && order > 0
        ).length > 0;

        return (
          <Tag
            color={order > 0 ? (isConflict ? 'red' : 'green') : 'orange'}
            style={{ cursor: record.status === 'deleted' ? 'not-allowed' : 'pointer' }}
            onClick={() => record.status !== 'deleted' && handleOrderClick(record)}
          >
            {order > 0 ? `Position ${order}${isConflict ? ' (Conflict!)' : ''}` : 'Not on homepage'}
          </Tag>
        );
      }
    },
    {
      title: 'Author',
      dataIndex: 'author_name',
      key: 'author_name',
      width: 150,
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'date',
      width: 120,
      render: date => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button 
            onClick={() => handleEdit(record)} 
            disabled={record.status === 'deleted'}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure to soft delete this blog?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            disabled={record.status === 'deleted'}
          >
            <Button 
              danger 
              disabled={record.status === 'deleted'}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading && blogs.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Blogs"
        description={error}
        type="error"
        showIcon
        style={{ margin: '24px' }}
      />
    );
  }

  return (
    <div style={{
      padding: '24px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%'
    }}>
      <h2 style={{ marginBottom: '16px' }}>Blogs Management</h2>

      <div style={{
        width: '100%',
        maxWidth: '1200px',
        marginBottom: '16px'
      }}>
        <Row justify="space-between" align="middle" gutter={16}>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setIsEditMode(false);
                setModalVisible(true);
              }}
            >
              Add Blog
            </Button>
          </Col>
          <Col>
            <Space>
              <Select
                value={deletedFilter}
                allowClear
                onChange={(value) => {
                  setDeletedFilter(value || '');
                  fetchData(1, pageSize, searchText, value || '', homepageFilter);
                }}
                style={{ width: 150 }}
              >
                <Option value="true">Deleted Only</Option>
              </Select>
              <Select
                value={homepageFilter}
                allowClear
                onChange={(value) => {
                  setHomepageFilter(value || '');
                  fetchData(1, pageSize, searchText, deletedFilter, value || '');
                }}
                style={{ width: 150 }}
              >
                <Option value="true">Homepage Only</Option>
              </Select>
              <Input.Search
                ref={searchInputRef}
                placeholder="Search blogs..."
                allowClear
                enterButton={isSearching ? <LoadingOutlined /> : <SearchOutlined />}
                size="large"
                style={{ width: 300 }}
                value={searchText}
                onChange={(e) => handleSearch(e.target.value, e)}
                onSearch={(value) => handleSearch(value, { type: 'search' })}
                onClear={handleClearSearch}
                loading={isSearching}
              />
            </Space>
          </Col>
        </Row>
      </div>

      <div style={{ width: '100%', maxWidth: '1200px' }}>
        {blogs.length === 0 && !loading ? (
          <Empty
            description={
              searchText || deletedFilter !== 'all' || homepageFilter !== 'all'
                ? 'No blogs found with the applied filters'
                : 'No blogs available'
            }
            style={{ margin: '40px 0' }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={blogs}
            loading={loading}
            rowKey="id"
            bordered
            scroll={{
              x: 'max-content',
              y: 'calc(100vh - 340px)'
            }}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              onChange: handlePageChange,
              onShowSizeChange: handlePageChange,
              showSizeChanger: true,
              pageSizeOptions: ['5', '10', '15', '20'],
              showTotal: (total, range) => (
                <span>
                  Showing {range[0]}-{range[1]} of {total} records
                </span>
              ),
              position: ['bottomCenter'],
            }}
            style={{ width: '100%' }}
          />
        )}
      </div>

      <Modal
        title={isEditMode ? 'Edit Blog' : 'Create Blog'}
        open={modalVisible}
        onCancel={resetModal}
        onOk={handleSubmit}
        width={800}
        confirmLoading={submitLoading}
        okButtonProps={{ loading: submitLoading }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Please input the title!' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="author_name"
            label="Author"
            rules={[{ required: true, message: 'Please input the author name!' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: 'Please input the category!' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="tags"
            label="Tags (comma-separated)"
          >
            <Input placeholder="Enter tags, separated by commas" />
          </Form.Item>

          <Form.Item
            label="Content"
            required
            validateStatus={!content ? 'error' : ''}
            help={!content ? 'Please add content to the blog' : ''}
          >
            <QuillEditor
              value={content}
              onChange={setContent}
              resetTemplate={isEditMode}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="image1"
                label="Thumbnail (453x256)"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                rules={[{
                  required: true,
                  validator: (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.reject('Please upload the thumbnail image');
                    }
                    return Promise.resolve();
                  }
                }]}
              >
                <Upload
                  customRequest={({ file, onSuccess }) => {
                    setTimeout(() => {
                      onSuccess({ url: URL.createObjectURL(file) });
                    }, 1000);
                  }}
                  listType="picture-card"
                  maxCount={1}
                  beforeUpload={(file) => beforeUploadValidation(file, 'thumbnail', 5, 453, 256)}
                  accept="image/*"
                >
                  <div>
                    <UploadOutlined />
                    <div>Upload</div>
                  </div>
                </Upload>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="image2"
                label="Main Image (930x384)"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                rules={[{
                  required: true,
                  validator: (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.reject('Please upload the main image');
                    }
                    return Promise.resolve();
                  }
                }]}
              >
                <Upload
                  customRequest={({ file, onSuccess }) => {
                    setTimeout(() => {
                      onSuccess({ url: URL.createObjectURL(file) });
                    }, 1000);
                  }}
                  listType="picture-card"
                  maxCount={1}
                  beforeUpload={(file) => beforeUploadValidation(file, 'main image', 5, 930, 384)}
                  accept="image/*"
                >
                  <div>
                    <UploadOutlined />
                    <div>Upload</div>
                  </div>
                </Upload>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="image3"
                label="Second Image (453x256)"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                rules={[{
                  required: true,
                  validator: (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.reject('Please upload the second image');
                    }
                    return Promise.resolve();
                  }
                }]}
              >
                <Upload
                  customRequest={({ file, onSuccess }) => {
                    setTimeout(() => {
                      onSuccess({ url: URL.createObjectURL(file) });
                    }, 1000);
                  }}
                  listType="picture-card"
                  maxCount={1}
                  beforeUpload={(file) => beforeUploadValidation(file, 'second image', 5, 453, 256)}
                  accept="image/*"
                >
                  <div>
                    <UploadOutlined />
                    <div>Upload</div>
                  </div>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Set Homepage Position"
        visible={orderModalVisible}
        onOk={handleOrderUpdate}
        onCancel={() => setOrderModalVisible(false)}
      >
        {selectedBlog && (
          <div>
            <p>Blog: <strong>{selectedBlog.title}</strong></p>
            <Select
              style={{ width: '100%' }}
              value={selectedBlog.homepageOrder || 0}
              onChange={(value) => setSelectedBlog({
                ...selectedBlog,
                homepageOrder: value
              })}
            >
              <Select.Option value={0}>Not on homepage</Select.Option>
              <Select.Option value={1}>Position 1</Select.Option>
              <Select.Option value={2}>Position 2</Select.Option>
              <Select.Option value={3}>Position 3</Select.Option>
              <Select.Option value={4}>Position 4</Select.Option>
            </Select>
            <p style={{ marginTop: 16, color: '#666' }}>
              Only 4 blogs can be featured on homepage. Selecting a position will replace any existing blog in that spot.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Blogs;